import axios from "./axios";

export interface EconomySettings {
  freeDailyQuota: number;
  singleUnlockPriceZ: number;
  creatorDailyPartLimit: number;
  creatorRevenueSharePercent: number;
  tokenToTryRate: number;
  creatorPayoutCurrencyCode: string;
}

export interface AdminPackageItem {
  id: string;
  name: string;
  dailyQuotaPremium: number;
  dailyQuotaFree: number;
  durationDays: number;
  priceZ: number;
  description?: string;
  imageUrl?: string;
  colorOptionIds?: string[];
  frameOptionIds?: string[];
  backgroundOptionIds?: string[];
}

export interface AdminZTokenPackageDto {
  id: string;
  name: string;
  tokenAmount: number;
  price: number;
  currency: string;
  description?: string;
  sortOrder: number;
  isActive: boolean;
  imageUrl?: string | null;
}

export interface ProfileColorOptionDto {
  id: string;
  name: string;
  hexCode: string;
  sortOrder: number;
  isActive: boolean;
}

export interface ProfileFrameOptionDto {
  id: string;
  name: string;
  imageUrl: string;
  sortOrder: number;
  isActive: boolean;
}

export interface ProfileBackgroundOptionDto {
  id: string;
  name: string;
  cssValue?: string;
  imageUrl?: string;
  previewUrl?: string;
  sortOrder: number;
  isActive: boolean;
  isDefault: boolean;
}

export async function getEconomySettings(): Promise<EconomySettings> {
  try {
    const res = await axios.get("/admin/economy/settings");
    return res.data as EconomySettings;
  } catch (err) {
    console.error("getEconomySettings error", err);
    return {
      freeDailyQuota: 10,
      singleUnlockPriceZ: 5,
      creatorDailyPartLimit: 3,
      creatorRevenueSharePercent: 65,
      tokenToTryRate: 1,
      creatorPayoutCurrencyCode: "TRY",
    };
  }
}

export async function getAdminPackages(): Promise<AdminPackageItem[]> {
  try {
    const res = await axios.get("/admin/packages");
    return Array.isArray(res.data) ? (res.data as AdminPackageItem[]) : [];
  } catch (err) {
    console.error("getAdminPackages error", err);
    return [];
  }
}

export async function saveEconomySettings(payload: EconomySettings): Promise<{ success: boolean }> {
  try {
    const res = await axios.post("/admin/economy/settings", payload);
    return { success: Boolean(res.data?.success ?? true) };
  } catch (err) {
    console.error("saveEconomySettings error", err);
    return { success: false };
  }
}

export async function updateAdminPackage(payload: {
  id: string;
  name?: string | null;
  dailyQuotaPremium: number;
  dailyQuotaFree: number;
  durationDays: number;
  priceZ: number;
  description?: string | null;
  imageUrl?: string | null;
  colorOptionIds?: string[] | null;
  frameOptionIds?: string[] | null;
  backgroundOptionIds?: string[] | null;
}): Promise<{ success: boolean }>
{
  try {
    const res = await axios.post("/admin/packages/update", payload);
    return { success: Boolean(res.data?.success) };
  } catch (err) {
    console.error("updateAdminPackage error", err);
    return { success: false };
  }
}

export async function createAdminPackage(payload: {
  name: string;
  dailyQuotaPremium: number;
  dailyQuotaFree: number;
  durationDays: number;
  priceZ: number;
  description?: string | null;
  imageUrl?: string | null;
  colorOptionIds?: string[] | null;
  frameOptionIds?: string[] | null;
  backgroundOptionIds?: string[] | null;
}): Promise<{ success: boolean; id?: string }>
{
  try {
    const res = await axios.post("/admin/packages/create", payload);
    return { success: Boolean(res.data?.success), id: res.data?.id };
  } catch (err) {
    console.error("createAdminPackage error", err);
    return { success: false };
  }
}

export async function uploadPackageImage(file: File): Promise<string | null> {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post("/admin/packages/upload-image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    const fileName = res.data?.fileName ?? res.data;
    if (typeof fileName === "string" && fileName.trim().length > 0) {
      return fileName.trim();
    }

    return null;
  } catch (err) {
    console.error("uploadPackageImage error", err);
    return null;
  }
}

export async function deleteAdminPackage(id: string): Promise<{ success: boolean }> {
  try {
    const res = await axios.delete(`/admin/packages/${id}`);
    return { success: Boolean(res.data?.success) };
  } catch (err) {
    console.error("deleteAdminPackage error", err);
    return { success: false };
  }
}

export async function getAdminZTokenPackages(includeInactive = true): Promise<AdminZTokenPackageDto[]> {
  try {
    const res = await axios.get(`/admin/z-token-packages?includeInactive=${includeInactive}`);
    return Array.isArray(res.data) ? (res.data as AdminZTokenPackageDto[]) : [];
  } catch (err) {
    console.error("getAdminZTokenPackages error", err);
    return [];
  }
}

export async function createAdminZTokenPackage(payload: {
  name: string;
  tokenAmount: number;
  price: number;
  currency: string;
  description?: string | null;
  sortOrder: number;
  isActive: boolean;
  imageUrl?: string | null;
}): Promise<{ success: boolean }> {
  try {
    const res = await axios.post("/admin/z-token-packages", payload);
    return { success: Boolean(res.data?.success) };
  } catch (err) {
    console.error("createAdminZTokenPackage error", err);
    return { success: false };
  }
}

export async function updateAdminZTokenPackage(id: string, payload: {
  name?: string;
  tokenAmount?: number;
  price?: number;
  currency?: string;
  description?: string | null;
  sortOrder?: number;
  isActive?: boolean;
  imageUrl?: string | null;
}): Promise<{ success: boolean }> {
  try {
    const res = await axios.put(`/admin/z-token-packages/${id}`, payload);
    return { success: Boolean(res.data?.success) };
  } catch (err) {
    console.error("updateAdminZTokenPackage error", err);
    return { success: false };
  }
}

export async function deleteAdminZTokenPackage(
  id: string,
  options?: { hardDelete?: boolean; confirmHardDelete?: boolean; reason?: string },
): Promise<{ success: boolean }> {
  const payload = {
    hardDelete: Boolean(options?.hardDelete),
    confirmHardDelete: Boolean(options?.confirmHardDelete),
    reason: options?.reason ?? "admin-ztoken-package-delete",
  };

  try {
    const res = await axios.delete(`/admin/z-token-packages/${id}`, { data: payload });
    return { success: Boolean(res.data?.success) };
  } catch (err) {
    console.error("deleteAdminZTokenPackage error", err);
    return { success: false };
  }
}

export async function getProfileColors(): Promise<ProfileColorOptionDto[]> {
  try {
    const res = await axios.get("/admin/profile-appearance/colors");
    return Array.isArray(res.data) ? (res.data as ProfileColorOptionDto[]) : [];
  } catch (err) {
    console.error("getProfileColors error", err);
    return [];
  }
}

export async function createProfileColor(payload: { name: string; hexCode: string; sortOrder: number; isActive: boolean }): Promise<boolean> {
  try {
    await axios.post("/admin/profile-appearance/colors", payload);
    return true;
  } catch (err) {
    console.error("createProfileColor error", err);
    return false;
  }
}

export async function updateProfileColor(id: string, payload: { name: string; hexCode: string; sortOrder: number; isActive: boolean }): Promise<boolean> {
  try {
    await axios.put(`/admin/profile-appearance/colors/${id}`, payload);
    return true;
  } catch (err) {
    console.error("updateProfileColor error", err);
    return false;
  }
}

export async function getProfileFrames(): Promise<ProfileFrameOptionDto[]> {
  try {
    const res = await axios.get("/admin/profile-appearance/frames");
    return Array.isArray(res.data) ? (res.data as ProfileFrameOptionDto[]) : [];
  } catch (err) {
    console.error("getProfileFrames error", err);
    return [];
  }
}

export async function getProfileBackgrounds(): Promise<ProfileBackgroundOptionDto[]> {
  try {
    const res = await axios.get("/admin/profile-appearance/backgrounds");
    return Array.isArray(res.data) ? (res.data as ProfileBackgroundOptionDto[]) : [];
  } catch (err) {
    console.error("getProfileBackgrounds error", err);
    return [];
  }
}

export async function createProfileBackground(payload: {
  name: string;
  cssValue?: string;
  imageUrl?: string;
  previewUrl?: string;
  sortOrder: number;
  isActive: boolean;
  isDefault: boolean;
}): Promise<boolean> {
  try {
    await axios.post("/admin/profile-appearance/backgrounds", payload);
    return true;
  } catch (err) {
    console.error("createProfileBackground error", err);
    return false;
  }
}

export async function updateProfileBackground(
  id: string,
  payload: {
    name: string;
    cssValue?: string;
    imageUrl?: string;
    previewUrl?: string;
    sortOrder: number;
    isActive: boolean;
    isDefault: boolean;
  },
): Promise<boolean> {
  try {
    await axios.put(`/admin/profile-appearance/backgrounds/${id}`, payload);
    return true;
  } catch (err) {
    console.error("updateProfileBackground error", err);
    return false;
  }
}

export async function uploadProfileBackgroundImage(file: File): Promise<string | null> {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const res = await axios.post("/admin/profile-appearance/backgrounds/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const fileName = res.data?.fileName ?? res.data;
    if (typeof fileName === "string" && fileName.trim().length > 0) {
      return fileName.trim();
    }
    return null;
  } catch (err) {
    console.error("uploadProfileBackgroundImage error", err);
    return null;
  }
}

export async function createProfileFrame(payload: { name: string; imageUrl: string; sortOrder: number; isActive: boolean }): Promise<boolean> {
  try {
    await axios.post("/admin/profile-appearance/frames", payload);
    return true;
  } catch (err) {
    console.error("createProfileFrame error", err);
    return false;
  }
}

export async function updateProfileFrame(id: string, payload: { name: string; imageUrl?: string; sortOrder: number; isActive: boolean }): Promise<boolean> {
  try {
    await axios.put(`/admin/profile-appearance/frames/${id}`, payload);
    return true;
  } catch (err) {
    console.error("updateProfileFrame error", err);
    return false;
  }
}

export async function uploadProfileFrameImage(file: File): Promise<string | null> {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const res = await axios.post("/admin/profile-appearance/frames/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const fileName = res.data?.fileName ?? res.data;
    if (typeof fileName === "string" && fileName.trim().length > 0) {
      return fileName.trim();
    }
    return null;
  } catch (err) {
    console.error("uploadProfileFrameImage error", err);
    return null;
  }
}
